// Screen preview of a page bitmap: area-averaged (box filter) to the exact
// pixel size it is shown at, so there is no aliasing or shimmering when the
// preview is scaled down, and dots keep their true 1.2 : 1 proportion
// (60 dots per inch across, 72 rows per inch down).
//
// A page is previewed as a full sheet of `rows` rows (like the PDF), even while
// it is still filling up. Rows the bitmap does not have yet are bare paper.
// Because ink is only ever added to paper, a growing page needs only its new
// rows drawn: previewRegion() renders just a band, dirtyTargetRows() says which.
//
// bm: { width, height, data } one byte per dot, 1 = black, height <= rows.
// Gray values: 255 = paper down to 0 = ink.

const ROWS_PER_INCH = 72;
const DOTS_PER_INCH = 60;

// Pixel size of the full-sheet preview for a pixel width W.
export function previewSize(dotsWide, rows, W) {
  const height = Math.max(1, Math.round((W * (rows / ROWS_PER_INCH)) / (dotsWide / DOTS_PER_INCH)));
  return { width: W, height };
}

// Weights of source cells [0, n) for each of `count` target pixels covering
// the same length: the overlap of the target pixel's span with every source
// cell it touches, normalised by the span length.
function axisWeights(n, count, from = 0, to = count) {
  const scale = n / count;
  const out = [];
  for (let t = from; t < to; t++) {
    const a = t * scale;
    const b = (t + 1) * scale;
    const first = Math.floor(a);
    const last = Math.min(n - 1, Math.ceil(b) - 1);
    const w = [];
    for (let s = first; s <= last; s++) {
      const overlap = Math.min(b, s + 1) - Math.max(a, s);
      if (overlap > 0) w.push([s, overlap / scale]);
    }
    out.push(w);
  }
  return out;
}

// Renders target rows [ty0, ty1) of the full-sheet preview. Returns
// { width, height: ty1 - ty0, gray }.
export function previewRegion(bm, W, rows, ty0, ty1) {
  const { height: fullH } = previewSize(bm.width, rows, W);
  ty0 = Math.max(0, ty0);
  ty1 = Math.min(fullH, ty1);
  const outH = Math.max(0, ty1 - ty0);
  const xw = axisWeights(bm.width, W);
  const yw = axisWeights(rows, fullH, ty0, ty1);

  // Only the source rows that feed this band, and never beyond the bitmap.
  let sMin = Infinity;
  let sMax = -1;
  for (const w of yw) for (const [s] of w) { sMin = Math.min(sMin, s); sMax = Math.max(sMax, s); }
  sMax = Math.min(sMax, bm.height - 1);
  const mid = new Map(); // source row -> Float32Array(W)
  for (let y = sMin; y <= sMax; y++) {
    const row = new Float32Array(W);
    const base = y * bm.width;
    for (let tx = 0; tx < W; tx++) {
      let sum = 0;
      for (const [s, w] of xw[tx]) if (bm.data[base + s]) sum += w;
      row[tx] = sum;
    }
    mid.set(y, row);
  }
  const gray = new Uint8Array(W * outH);
  for (let i = 0; i < outH; i++) {
    for (let tx = 0; tx < W; tx++) {
      let sum = 0;
      for (const [s, w] of yw[i]) {
        const row = mid.get(s);
        if (row) sum += row[tx] * w;
      }
      gray[i * W + tx] = Math.round(255 * (1 - Math.min(1, sum)));
    }
  }
  return { width: W, height: outH, gray };
}

// The whole sheet.
export function previewGray(bm, W, rows = bm.height) {
  return previewRegion(bm, W, rows, 0, previewSize(bm.width, rows, W).height);
}

// Which target rows [ty0, ty1) differ between the previous bitmap of a page and
// the new one, or null if nothing changed. Compares the data, so it is exact
// even when something other than appended ink changed (e.g. reverse video).
export function dirtyTargetRows(prev, bm, W, rows) {
  const n = Math.min(prev.data.length, bm.data.length);
  let i = 0;
  while (i < n && prev.data[i] === bm.data[i]) i++;
  if (i === n && prev.data.length === bm.data.length) return null;
  // last differing cell, scanning back from the end
  let j = Math.max(prev.data.length, bm.data.length) - 1;
  while (j >= 0 && j < n && prev.data[j] === bm.data[j]) j--;
  const r0 = Math.floor(i / bm.width);
  const r1 = Math.floor(j / bm.width) + 1;
  const { height: fullH } = previewSize(bm.width, rows, W);
  const scale = fullH / rows;
  // one extra target row each side: the filter's footprint straddles row borders
  return [Math.max(0, Math.floor(r0 * scale) - 1), Math.min(fullH, Math.ceil(r1 * scale) + 1)];
}
